// ABOUTME: Type definitions for chart components with system entity support
// ABOUTME: Provides type safety for pie charts, analytics data, and mixed entity types

/**
 * Base entity that can be displayed in charts (child, adult, or system)
 */
export interface ChartEntity {
  id: number;
  name: string;
  profileImageUrl?: string | null;
  isSystemCategory?: boolean;
}

/**
 * Data point for pie chart visualization
 */
export interface PieChartDataPoint {
  name: string;
  value: number;
  id: number;
  childId?: number; // For backward compatibility
  profileImageUrl?: string | null;
  color: string;
  isSystemCategory?: boolean;
}

/**
 * Entity types supported by the chart system
 */
export type EntityType = 'child' | 'adult';

/**
 * Data types that can be visualized in charts
 */
export type ChartDataType = 'stars' | 'hearts' | 'behaviors' | 'progress';

/**
 * System entity IDs and constants
 */
export const SYSTEM_ENTITY_IDS = {
  VOICE_RECORDING: -1
} as const;

/**
 * Adult logging data from analytics API
 */
export interface AdultLoggingData {
  userId: number;
  name: string;
  profileImageUrl?: string | null;
  behaviorCount: number;
}

/**
 * Analytics response structure
 */
export interface AnalyticsResponse {
  timeWindow: {
    startDate: string;
    endDate: string;
    timezone: string;
    label: string;
  };
  metrics: {
    adultsLogging: AdultLoggingData[];
  };
}

/**
 * Chart color palettes — index-based, distinguishing entities (children/adults) in a chart,
 * not brand colors. Same categorical exception as utils/childColorUtils.ts and utils/childColors.ts;
 * left as literals deliberately, not routed through theme/tokens.ts.
 */
export const CHART_COLORS = {
  CHILDREN: [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
    '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
  ],
  ADULTS: [
    '#2196F3', '#9C27B0', '#FF9800', '#4CAF50', '#F44336',
    '#00BCD4', '#8BC34A', '#FFC107', '#E91E63', '#607D8B'
  ]
} as const;

/**
 * Type guard to check if an entity is a system entity
 */
export const isSystemEntity = (entity: ChartEntity): boolean => {
  return entity.id === SYSTEM_ENTITY_IDS.VOICE_RECORDING || entity.isSystemCategory === true;
};